/**
 * PostHog feature-flag keys for client-side evaluation via
 * `useFeatureFlagVariantKey` / `posthog.getFeatureFlag`.
 *
 * SSR no longer calls getAllFlags (TTFB). PostHogProvider sets
 * `advanced_disable_feature_flags: false` so the browser `/flags` + identify()
 * reload decide variants. Gate UI on `bootstrapComplete` (or a clientReady
 * latch) so first paint matches SSR safe defaults — intentional flicker after
 * hydrate is OK; React #418 is not.
 *
 * Prefer multivariate `'test'` / `'control'`. Branch on `=== 'test'`, never
 * `useFeatureFlagEnabled` on multivariate flags.
 */
// Co-located with the rest of the PostHog code so consumers find feature flags
// at the same barrel as the provider + hook. More named exports may be added
// (typed flag accessors, etc.); a default export would force anonymous imports.
// eslint-disable-next-line import/prefer-default-export
export const POSTHOG_FEATURE_FLAGS = {
  AI_GOTS: 'ai_gots',
  // Single-Circle Beta release gate (useCirclesEnabled, alongside the
  // FEATURES_CIRCLES_ENABLED config killswitch): the no-deploy lever to turn
  // Circles on/off and control future rollout. Widen the release conditions in
  // PostHog to roll out; set 0%/disable to turn the feature off without a
  // deploy. Replaces the retired community-circles-employee flag (same
  // tester-email targeting @ 100% initially). Nav visibility additionally
  // requires membership (circles.length > 0 in CirclesNavEntry).
  COMMUNITY_CIRCLES_BETA: 'community-circles-beta',
  // Multi-circle header UI gate (CirclesDrawerHeader): temporary tester lever
  // for the circle picker / "find more circles" surfaces, layered ON TOP of the
  // COMMUNITY_CIRCLES_BETA feature gate — it never grants Circles access by
  // itself. The UI additionally requires membership in >1 circle.
  COMMUNITY_CIRCLES_EMPLOYEE: 'community-circles-employee',
  // CO-518 Community Activity release gate (useCommunityActivityEnabled,
  // alongside the FEATURES_COMMUNITY_ACTIVITY config killswitch): the no-deploy
  // lever for the owner-only Activity tab + the author-facing comment
  // moderation status badges. BOTH layers must pass. Widen the release
  // conditions in PostHog to roll out; set 0%/disable to turn the feature off
  // without a deploy. Starts at Gaia employees @ 100%. Real held/rejected
  // states only appear once the new gaia-brooklyn-api comment statuses ship.
  COMMUNITY_ACTIVITY_BETA: 'community-activity-beta',
  // AI-502: AI Search A/B experiment (multivariate: control/test)
  AI_SEARCH_WEB: 'ai-search-web',
  // AP-19192: Unified search A/B/C for entitled global search
  // (control / test_sage_top / test_unified)
  UNIFIED_SEARCH_WEB: 'unified-search-web',
  // AI-761: Daily Divination Card per-feature gates
  AI_DIVINATION_TAROT: 'ai-divination-tarot',
  AI_DIVINATION_ORACLE_CARDS: 'ai-divination-oracle',
  AI_DIVINATION_HOROSCOPE: 'ai-divination-horoscope',
  // AI-1205: Prompt Library route/page gate (multivariate: control/test). Gates
  // the /aichat/conversation/prompts sub-route + its sidebar entry. Read the
  // variant KEY (=== 'test'), not useFeatureFlagEnabled, so the 'control' arm
  // stays in the holdout.
  AI_PROMPT_LIBRARY: 'ai-prompt-library',
  // AI-1157: Member Home prompt module gate (multivariate: control/test). This
  // is the SECOND of two independent levers — the module also has to be enabled
  // as an `ai-chat-prompts` section in the Page Manager admin app. Read the
  // variant KEY (=== 'test'), never useFeatureFlagEnabled.
  //
  // No bootstrap work is needed for this one: its release condition targets a
  // dynamic cohort matched on `person.email`, the SSR bootstrap already sends
  // `email` (posthog-bootstrap/middleware.js), and getPostHogClient() is built
  // without a personalApiKey — so posthog-node cannot locally evaluate and
  // PostHog resolves the cohort remotely. Local evaluation is where property
  // cohorts break; this app never uses it.
  AI_PROMPT_LIBRARY_MH_MODULE: 'ai-promptlibrary-mh-module',
  // AI-887: Member Home "Select Your Guide" persona picker (multivariate:
  // control/test). Read the variant KEY (=== 'test'), never
  // useFeatureFlagEnabled. This flag is the feature's only kill switch — the
  // server's `shouldShowPersonaSelect` decides whether an eligible member has
  // already seen the dialog, but it cannot turn the feature off.
  //
  // Audience is controlled ENTIRELY by this flag's targeting: the picker must
  // never render with a single option, so it ships to English speakers only
  // (AI-394 keeps Architect+ English-only). There is deliberately no locale
  // branch in the component. The `language` person property is already sent to
  // getAllFlags by posthog-bootstrap/middleware.js — note it is omit-if-absent,
  // so a `language = en` condition excludes members with no language set.
  AI_PERSONA_MODULE: 'ai-persona-module',
  // Onboarding-offer release gate for the /get-started "join a circle" card,
  // multivariate ('test' / 'control'). Splits the offer off the
  // COMMUNITY_CIRCLES_BETA gate so "stop offering circles to new users" and
  // "keep existing members' drawer working" are independent no-deploy levers
  // (CirclesNavEntry's membership check rides the beta flag, so disabling THAT
  // one strips members). Read it ONLY through `Gating/useCirclesOfferEnabled`,
  // which layers it on useCirclesEnabled({ requireDesktop: true }) and branches
  // strictly on === 'test' — never useFeatureFlagEnabled. Release gate, not an
  // experiment: no experiment_exposure capture at the read.
  COMMUNITY_CIRCLES_ONBOARDING_OFFER: 'community-circles-onboarding-offer',
  // CO-515 beta-2 cohort gate, multivariate (pinned by useCirclesActiveCohort):
  // 'test' = the wave-1 tenured cohort (sees the tenured rooms AND the nav
  // discovery menu), 'participant' = the person-property lane
  // (circles_participant = true, has ever joined a circle — menu access only),
  // 'control' = holdout. Read it ONLY through `Gating/useCirclesActiveCohort`,
  // which splits it into a strict tenured-visibility gate (=== 'test') and a
  // tolerant menu-access gate — never inline, never via useFeatureFlagEnabled.
  // NOTE: this cohort must ALSO pass `community-circles-beta` — `useCirclesEnabled`
  // gates the `myCommunityFeeds` read that decides "is this user in no circle",
  // so outside that flag `circles` is permanently [] and the menu never renders.
  CIRCLES_BETA_ACTIVE_COHORTS: 'circles-beta-active-cohorts',
}

/** PostHog flag key for session-recording eligibility (not in POSTHOG_FEATURE_FLAGS). */
export const POSTHOG_SESSION_RECORDING_FLAG = 'enable-session-recording'
